import React, { useState, useEffect } from 'react';
import {
  Card, Space, Button, Tag, Descriptions, Statistic, Row, Col, 
  message, Typography, Divider, Popconfirm, Alert, Form, Input, InputNumber,
  Table, Modal, Select, Spin
} from 'antd';
import {
  ReloadOutlined, SendOutlined, RobotOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SyncOutlined, ClockCircleOutlined, RetweetOutlined
} from '@ant-design/icons';
import { useI18n } from '../hooks/useI18n';
import { ibuildProxyService } from '../services/ibuildProxyService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface AgentStats {
  pendingRequests: number;
  completedRequests: number;
  failedRequests: number;
  pendingRetries: number;
  totalRequests: number;
}

interface RequestStatus {
  requestId: string;
  tenantCode: string;
  status: string;
  messageChunks: number;
  streamMessages: number;
  createdAt: string;
  updatedAt: string;
}

const IBuildProxyAgentManagement: React.FC = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<RequestStatus | null>(null);
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const result = await ibuildProxyService.getAgentStats();
      
      if (result.success && result.data) {
        setStats(result.data);
      } else {
        message.error(`加载统计信息失败: ${result.error || result.message}`);
      }
    } catch (error: any) {
      message.error(`加载统计信息失败: ${error.message}`);
      console.error('统计信息加载失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      const values = await form.validateFields();
      setSending(true);
      
      const agentRequest = {
        robotKey: values.robotKey,
        connectInfo: values.connectInfo,
        message: values.message,
        tenantId: values.tenantId,
        userId: values.userId,
        requestId: values.requestId,
        sessionId: values.sessionId,
        ...(values.proxiedAgentPlatform ? { proxiedAgentPlatform: JSON.parse(values.proxiedAgentPlatform) } : {})
      };

      const result = await ibuildProxyService.sendAgentMessage(agentRequest);
      
      if (result.success) {
        message.success(`消息发送成功！Request ID: ${result.requestId}`);
        form.resetFields();
        setSendModalVisible(false);
        await loadStats();
      } else {
        message.error(result.error || result.message || '发送消息失败');
      }
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error(`发送消息失败: ${error.message}`);
      console.error('发送消息失败:', error);
    } finally {
      setSending(false);
    }
  };

  const handleCheckStatus = async () => {
    try {
      const values = await statusForm.validateFields();
      setStatusLoading(true);
      
      const result = await ibuildProxyService.getAgentStatus(values.requestId);
      
      if (result.success && result.data) {
        setRequestStatus(result.data);
        setStatusModalVisible(true);
      } else {
        message.error(result.error || result.message || '查询状态失败');
      }
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error(`查询状态失败: ${error.message}`);
      console.error('查询状态失败:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleRetry = async (id: number) => {
    try {
      const result = await ibuildProxyService.retryAgentMessage(id);
      
      if (result.success) {
        message.success('重试成功');
        await loadStats();
      } else {
        message.error(result.error || result.message || '重试失败');
      }
    } catch (error: any) {
      message.error(`重试失败: ${error.message}`);
      console.error('重试失败:', error);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      pending: { color: 'default', text: '待处理' },
      processing: { color: 'processing', text: '处理中' },
      completed: { color: 'success', text: '已完成' },
      failed: { color: 'error', text: '失败' },
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>IBuild Proxy Agent 管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setSendModalVisible(true)}
            >
              发送消息
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadStats}
              loading={loading}
            >
              刷新统计
            </Button>
          </Space>
        }
      >
        {/* 统计信息 */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="待处理请求"
                  value={stats.pendingRequests}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="已完成请求"
                  value={stats.completedRequests}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ fontSize: '24px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="失败请求"
                  value={stats.failedRequests}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ fontSize: '24px', color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="待重试"
                  value={stats.pendingRetries}
                  prefix={<RetweetOutlined />}
                  valueStyle={{ fontSize: '24px', color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card>
                <Statistic
                  title="总请求数"
                  value={stats.totalRequests || (stats.pendingRequests + stats.completedRequests + stats.failedRequests)}
                  prefix={<RobotOutlined />}
                  valueStyle={{ fontSize: '32px' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 查询状态 */}
        <Card title="查询请求状态" size="small" style={{ marginBottom: 24 }}>
          <Form
            form={statusForm}
            layout="inline"
            onFinish={handleCheckStatus}
          >
            <Form.Item
              label="Request ID"
              name="requestId"
              rules={[{ required: true, message: '请输入 Request ID' }]}
            >
              <Input
                placeholder="请输入 Request ID"
                style={{ width: 300 }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={statusLoading}
                icon={<SyncOutlined />}
              >
                查询状态
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {loading && !stats && (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        )}
      </Card>

      {/* 发送消息模态框 */}
      <Modal
        title="发送消息至 IBuild Agent"
        open={sendModalVisible}
        onOk={handleSendMessage}
        onCancel={() => {
          setSendModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={sending}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Robot Key"
            name="robotKey"
            rules={[{ required: true, message: '请输入 Robot Key' }]}
          >
            <Input placeholder="请输入 Robot Key" />
          </Form.Item>
          <Form.Item
            label="Connect Info"
            name="connectInfo"
            rules={[{ required: true, message: '请输入 Connect Info' }]}
          >
            <Input placeholder="请输入 Connect Info" />
          </Form.Item>
          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: '请输入 Message' }]}
          >
            <TextArea rows={4} placeholder="请输入消息内容" />
          </Form.Item>
          <Form.Item
            label="Tenant ID"
            name="tenantId"
            rules={[{ required: true, message: '请输入 Tenant ID' }]}
          >
            <Input placeholder="请输入 Tenant ID" />
          </Form.Item>
          <Form.Item
            label="User ID"
            name="userId"
            rules={[{ required: true, message: '请输入 User ID' }]}
          >
            <Input placeholder="请输入 User ID" />
          </Form.Item>
          <Form.Item
            label="Request ID (可选)"
            name="requestId"
          >
            <Input placeholder="留空则自动生成" />
          </Form.Item>
          <Form.Item
            label="Session ID (可选)"
            name="sessionId"
          >
            <Input placeholder="请输入 Session ID" />
          </Form.Item>
          <Form.Item
            label="Proxied Agent Platform (可选, JSON格式)"
            name="proxiedAgentPlatform"
            tooltip="如果需要使用动态配置通道，请以 JSON 格式提供 proxiedAgentPlatform 配置"
          >
            <TextArea rows={6} placeholder='{"id": 1, "service_url": "...", "auth_type": "api_key", ...}' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 请求状态详情模态框 */}
      <Modal
        title="请求状态详情"
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          setRequestStatus(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setStatusModalVisible(false);
            setRequestStatus(null);
          }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {requestStatus && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Request ID" span={2}>
              <Text code>{requestStatus.requestId}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tenant Code">
              {requestStatus.tenantCode}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(requestStatus.status)}
            </Descriptions.Item>
            <Descriptions.Item label="消息块数">
              {requestStatus.messageChunks}
            </Descriptions.Item>
            <Descriptions.Item label="流式消息数">
              {requestStatus.streamMessages}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(requestStatus.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {dayjs(requestStatus.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default IBuildProxyAgentManagement;

